import { SCHEDULE_ITEM_STATUS } from "components/AppointmentsList/constants/scheduleItemStatus";

// Background blocks (exceptions, unavailable time, empty slots) are laid out
// full width; appointment cards sit over them. Only the cards are laned.
export const isExceptionItem = (item) =>
  item.status === SCHEDULE_ITEM_STATUS.STANDARD_EXCEPTION ||
  item.status === SCHEDULE_ITEM_STATUS.RECURRING_EXCEPTION;

// "Unavailable" blocks are context, not bookings: they cover whole stretches of
// the day, so laning them against real appointments would squeeze both into
// halves. They stay full width behind everything, like an exception.
export const isUnavailableBlockItem = (item) =>
  item.status === SCHEDULE_ITEM_STATUS.UNAVAILABLE && !!item.meeting;

export const isAppointmentCardItem = (item) =>
  item.status === SCHEDULE_ITEM_STATUS.CONSULTATION;

const getStartRow = (item) => item.slotIndex ?? 0;
const getEndRow = (item) => getStartRow(item) + (item.rowSpan || 1);

const overlapsRange = (item, startRow, endRow) =>
  getStartRow(item) < endRow && getEndRow(item) > startRow;

// Greedy interval colouring: an item takes the first lane whose previous
// occupant has already finished, otherwise it opens a new one.
const assignLanesWithinCluster = (cluster) => {
  const laneEndRows = [];

  const laneIndexes = cluster.map((item) => {
    const freeLane = laneEndRows.findIndex(
      (endRow) => endRow <= getStartRow(item)
    );
    const laneIndex = freeLane === -1 ? laneEndRows.length : freeLane;

    laneEndRows[laneIndex] = getEndRow(item);

    return laneIndex;
  });

  return { laneIndexes, laneCount: laneEndRows.length };
};

// Split the cards into clusters of transitively overlapping row ranges. Every
// card in a cluster shares the same lane count so their edges line up.
const buildClusters = (cards) => {
  const clusters = [];
  let currentCluster = [];
  let currentClusterEndRow = -Infinity;

  cards.forEach((card) => {
    const startsNewCluster = getStartRow(card) >= currentClusterEndRow;

    if (startsNewCluster && currentCluster.length > 0) {
      clusters.push(currentCluster);
      currentCluster = [];
    }

    currentCluster.push(card);
    currentClusterEndRow = Math.max(currentClusterEndRow, getEndRow(card));
  });

  if (currentCluster.length > 0) {
    clusters.push(currentCluster);
  }

  return clusters;
};

/**
 * Annotates appointment cards with the lane they occupy so overlapping items
 * sit side by side instead of stacking on top of each other.
 *
 * Cards whose cluster runs over an availability exception also reserve a
 * gutter, keeping the exception's own actions menu clickable (KIN-1591).
 */
export const assignOverlapLanes = (items) => {
  const exceptions = items.filter(isExceptionItem);
  const unavailableBlocks = items.filter(isUnavailableBlockItem);

  const cards = items
    .filter(isAppointmentCardItem)
    .sort((first, second) =>
      getStartRow(first) === getStartRow(second)
        ? getEndRow(first) - getEndRow(second)
        : getStartRow(first) - getStartRow(second)
    );

  // How many blocks an item is stacked on top of. Nothing underneath means
  // depth 0 and full width — items only step in where they actually overlap.
  const depthByItem = new Map();

  exceptions.forEach((exception) => {
    const sitsOnUnavailable = unavailableBlocks.some((block) =>
      overlapsRange(block, getStartRow(exception), getEndRow(exception))
    );

    depthByItem.set(exception, sitsOnUnavailable ? 1 : 0);
  });

  const laneByItem = new Map();

  buildClusters(cards).forEach((cluster) => {
    const { laneIndexes, laneCount } = assignLanesWithinCluster(cluster);

    const clusterStartRow = Math.min(...cluster.map(getStartRow));
    const clusterEndRow = Math.max(...cluster.map(getEndRow));

    const coveredExceptions = exceptions.filter((exception) =>
      overlapsRange(exception, clusterStartRow, clusterEndRow)
    );

    // A card steps in one level past the deepest exception it covers.
    const depth = coveredExceptions.length
      ? Math.max(...coveredExceptions.map((item) => depthByItem.get(item))) + 1
      : 0;

    cluster.forEach((card, indexInCluster) => {
      depthByItem.set(card, depth);
      laneByItem.set(card, {
        index: laneIndexes[indexInCluster],
        count: laneCount,
        overlapsException: coveredExceptions.length > 0,
      });
    });
  });

  return items.map((item) => {
    const depth = depthByItem.get(item);
    const lane = laneByItem.get(item);

    if (depth === undefined && !lane) {
      return item;
    }

    return { ...item, depth: depth || 0, ...(lane ? { lane } : {}) };
  });
};
